/**
 * Registration Page Component
 * Supports email/password registration with Supabase
 */

import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { validatePassword } from '../../types/auth';
import { Mail, Lock, User, Eye, EyeOff, Loader2, AlertCircle, Check, X, CheckCircle } from 'lucide-react';
import ShieldIcon from '../ShieldIcon';
import { prefetchForDashboard, prefetchDashboardModules } from '../../utils/prefetch';
import { resolvePostLoginPath } from '../../lib/postLoginRedirect';

const RegisterPage: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const location = useLocation();
    const { signUp, signInWithGoogle, isAuthenticated, isLoading, error, clearError } = useAuth();

    const nextParam = searchParams.get('next');
    const fromTry = Boolean((location.state as { fromTry?: boolean } | null)?.fromTry);
    const postAuthPath = resolvePostLoginPath(nextParam, fromTry ? '/try' : undefined);
    const hasPrefetchedRef = useRef(false);

    const [fullName, setFullName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [consentGiven, setConsentGiven] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [localError, setLocalError] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [registrationSuccess, setRegistrationSuccess] = useState(false);

    // Password validation
    const passwordValidation = validatePassword(password);

    // Prefetch dashboard modules when register page mounts
    useEffect(() => {
        prefetchForDashboard();
    }, []);

    // Aggressively prefetch when user starts typing
    useEffect(() => {
        if ((email || password) && !hasPrefetchedRef.current) {
            hasPrefetchedRef.current = true;
            prefetchDashboardModules();
        }
    }, [email, password]);

    // Redirect if already authenticated
    useEffect(() => {
        if (isAuthenticated) {
            navigate(postAuthPath, { replace: true });
        }
    }, [isAuthenticated, navigate, postAuthPath]);

    // Clear errors on input change
    useEffect(() => {
        if (error) clearError();
        setLocalError(null);
    }, [email, password, confirmPassword, fullName]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLocalError(null);

        // Validation
        if (!email || !password || !confirmPassword) {
            setLocalError(t('auth.errors.allFieldsRequired'));
            return;
        }

        if (!passwordValidation.isValid) {
            setLocalError(t('auth.errors.passwordRequirements'));
            return;
        }

        if (password !== confirmPassword) {
            setLocalError(t('auth.errors.passwordMismatch'));
            return;
        }

        if (!consentGiven) {
            setLocalError(t('auth.errors.consentRequired'));
            return;
        }

        setIsSubmitting(true);
        try {
            await signUp(email, password, fullName || undefined);
            setRegistrationSuccess(true);
        } catch (err) {
            // Error is already set in context
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleGoogleSignUp = async () => {
        setLocalError(null);
        try {
            await signInWithGoogle(`${window.location.origin}${postAuthPath}`);
        } catch (err) {
            // Error is handled in context
        }
    };

    const displayError = localError || error;

    // Success message after registration
    if (registrationSuccess) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-secondary-50 via-white to-brand-100/35 dark:from-secondary-900 dark:via-secondary-900 dark:to-brand-900/25 py-8 sm:py-12 px-4 sm:px-6 lg:px-8">
                <div className="max-w-md w-full">
                    <div className="bg-white dark:bg-secondary-800 rounded-2xl shadow-xl p-6 sm:p-8 text-center">
                        <div className="mx-auto w-16 h-16 bg-success-100 dark:bg-success-900/50 rounded-full flex items-center justify-center mb-6">
                            <CheckCircle className="h-8 w-8 text-success-600 dark:text-success-400" />
                        </div>
                        <h2 className="text-2xl font-bold text-secondary-800 dark:text-secondary-100 mb-2">
                            Check your email
                        </h2>
                        <p className="text-secondary-600 dark:text-secondary-400 mb-6">
                            We've sent a confirmation link to <strong>{email}</strong>. 
                            Please click the link to verify your account.
                        </p>
                        <Link
                            to={
                                postAuthPath !== '/app'
                                    ? `/login?next=${encodeURIComponent(postAuthPath)}`
                                    : '/login'
                            }
                            className="inline-flex items-center justify-center rounded-xl border border-transparent bg-brand-500 px-6 py-3 font-medium text-white transition-colors hover:bg-brand-600 dark:bg-brand-600 dark:hover:bg-brand-500"
                        >
                            Go to Login
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-secondary-50 via-white to-brand-100/35 dark:from-secondary-900 dark:via-secondary-900 dark:to-brand-900/25 py-8 sm:py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-md w-full">
                {/* Logo and Title */}
                <div className="text-center mb-8">
                    <Link to="/" className="inline-flex items-center justify-center group">
                        <div className="p-3 rounded-2xl bg-white dark:bg-secondary-800 shadow-lg group-hover:shadow-xl transition-shadow duration-200">
                            <ShieldIcon className="h-12 w-12" />
                        </div>
                    </Link>
                    <h2 className="mt-6 text-3xl font-bold text-secondary-800 dark:text-secondary-100">
                        {t('auth.register.title')}
                    </h2>
                    <p className="mt-2 text-sm text-secondary-600 dark:text-secondary-400">
                        {t('auth.register.subtitle')}{' '}
                        <Link
                            to={nextParam ? `/login?next=${encodeURIComponent(nextParam)}` : '/login'}
                            className="font-medium text-brand-600 transition-colors hover:text-brand-500 dark:text-brand-400 dark:hover:text-brand-300"
                        >
                            {t('auth.register.signIn')}
                        </Link>
                    </p>
                </div>

                {/* Registration Form */}
                <div className="bg-white dark:bg-secondary-800 rounded-2xl shadow-xl p-6 sm:p-8">
                    {displayError && (
                        <div className="mb-6 p-4 bg-danger-50 dark:bg-danger-900/30 border border-danger-200 dark:border-danger-700 rounded-xl flex items-start">
                            <AlertCircle className="h-5 w-5 text-danger-600 dark:text-danger-400 mr-3 flex-shrink-0 mt-0.5" aria-hidden />
                            <span className="text-sm text-danger-800 dark:text-danger-200">{displayError}</span>
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-5">
                        {/* Full Name Input */}
                        <div>
                            <label htmlFor="fullName" className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                                {t('auth.fields.fullName')} <span className="text-secondary-400 dark:text-secondary-500">({t('common.optional')})</span>
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <User className="h-5 w-5 text-secondary-400 dark:text-secondary-500" />
                                </div>
                                <input
                                    id="fullName"
                                    type="text"
                                    value={fullName}
                                    onChange={(e) => setFullName(e.target.value)}
                                    className="block w-full pl-10 pr-4 py-3 border border-secondary-300 dark:border-secondary-600 rounded-xl bg-white dark:bg-secondary-700 text-secondary-900 dark:text-secondary-100 focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-colors"
                                    placeholder="John Doe"
                                    autoComplete="name"
                                    disabled={isSubmitting}
                                />
                            </div>
                        </div>

                        {/* Email Input */}
                        <div>
                            <label htmlFor="email" className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                                {t('auth.fields.email')} <span className="text-danger-600 dark:text-danger-400">*</span>
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Mail className="h-5 w-5 text-secondary-400 dark:text-secondary-500" />
                                </div>
                                <input
                                    id="email"
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="block w-full pl-10 pr-4 py-3 border border-secondary-300 dark:border-secondary-600 rounded-xl bg-white dark:bg-secondary-700 text-secondary-900 dark:text-secondary-100 focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-colors"
                                    placeholder="you@example.com"
                                    autoComplete="email"
                                    required
                                    disabled={isSubmitting}
                                />
                            </div>
                        </div>

                        {/* Password Input */}
                        <div>
                            <label htmlFor="password" className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                                {t('auth.fields.password')} <span className="text-danger-600 dark:text-danger-400">*</span>
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Lock className="h-5 w-5 text-secondary-400 dark:text-secondary-500" />
                                </div>
                                <input
                                    id="password"
                                    type={showPassword ? 'text' : 'password'}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="block w-full pl-10 pr-12 py-3 border border-secondary-300 dark:border-secondary-600 rounded-xl bg-white dark:bg-secondary-700 text-secondary-900 dark:text-secondary-100 focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-colors"
                                    placeholder="••••••••"
                                    autoComplete="new-password"
                                    required
                                    disabled={isSubmitting}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-secondary-400 hover:text-secondary-600 dark:hover:text-secondary-300"
                                >
                                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                                </button>
                            </div>

                            {/* Password Requirements */}
                            {password && (
                                <div className="mt-3 p-3 bg-secondary-50 dark:bg-secondary-700 rounded-lg space-y-1">
                                    <PasswordRequirement
                                        met={passwordValidation.minLength}
                                        text={t('auth.passwordRequirements.minLength')}
                                    />
                                    <PasswordRequirement
                                        met={passwordValidation.hasUppercase}
                                        text={t('auth.passwordRequirements.uppercase')}
                                    />
                                    <PasswordRequirement
                                        met={passwordValidation.hasLowercase}
                                        text={t('auth.passwordRequirements.lowercase')}
                                    />
                                    <PasswordRequirement
                                        met={passwordValidation.hasNumber}
                                        text={t('auth.passwordRequirements.number')}
                                    />
                                </div>
                            )}
                        </div>

                        {/* Confirm Password Input */}
                        <div>
                            <label htmlFor="confirmPassword" className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                                {t('auth.fields.confirmPassword')} <span className="text-danger-600 dark:text-danger-400">*</span>
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Lock className="h-5 w-5 text-secondary-400 dark:text-secondary-500" />
                                </div>
                                <input
                                    id="confirmPassword"
                                    type={showConfirmPassword ? 'text' : 'password'}
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    className={`block w-full pl-10 pr-12 py-3 border rounded-xl bg-white dark:bg-secondary-700 text-secondary-900 dark:text-secondary-100 focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-colors ${
                                        confirmPassword && password !== confirmPassword
                                            ? 'border-danger-300 dark:border-danger-600 bg-danger-50 dark:bg-danger-900/30'
                                            : 'border-secondary-300 dark:border-secondary-600'
                                    }`}
                                    placeholder="••••••••"
                                    autoComplete="new-password"
                                    required
                                    disabled={isSubmitting}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-secondary-400 hover:text-secondary-600 dark:hover:text-secondary-300"
                                >
                                    {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                                </button>
                            </div>
                            {confirmPassword && password !== confirmPassword && (
                                <p className="mt-1 text-sm text-danger-700 dark:text-danger-300">{t('auth.errors.passwordMismatch')}</p>
                            )}
                        </div>

                        {/* Consent checkbox — applies to email/password signup only */}
                        <div className="flex items-start">
                            <div className="flex items-center h-5">
                                <input
                                    id="consent"
                                    type="checkbox"
                                    checked={consentGiven}
                                    onChange={(e) => setConsentGiven(e.target.checked)}
                                    className="qm-checkbox"
                                    required
                                    disabled={isSubmitting}
                                />
                            </div>
                            <div className="ml-3 text-sm">
                                <label
                                    htmlFor="consent"
                                    className="text-secondary-700 dark:text-secondary-200 leading-relaxed"
                                >
                                    {t('auth.consent.text')}{' '}
                                    <Link
                                        to="/privacy"
                                        className="text-brand-600 hover:text-brand-500 dark:text-brand-400 dark:hover:text-brand-300"
                                        target="_blank"
                                    >
                                        {t('auth.consent.privacyPolicy')}
                                    </Link>
                                    {' '}{t('auth.consent.and')}{' '}
                                    <Link
                                        to="/terms"
                                        className="text-brand-600 hover:text-brand-500 dark:text-brand-400 dark:hover:text-brand-300"
                                        target="_blank"
                                    >
                                        {t('auth.consent.termsOfService')}
                                    </Link>
                                    . <span className="text-danger-600 dark:text-danger-400">*</span>
                                </label>
                            </div>
                        </div>

                        {/* Submit Button */}
                        <button
                            type="submit"
                            disabled={isSubmitting || isLoading || !consentGiven}
                            className="flex w-full items-center justify-center rounded-xl border border-transparent bg-brand-500 px-4 py-3 font-medium text-white transition-colors hover:bg-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-brand-600 dark:hover:bg-brand-500"
                        >
                            {isSubmitting ? (
                                <>
                                    <Loader2 className="h-5 w-5 animate-spin mr-2" />
                                    {t('auth.register.creatingAccount')}
                                </>
                            ) : (
                                t('auth.register.createAccount')
                            )}
                        </button>
                    </form>

                    {/* Divider */}
                    <div className="mt-6">
                        <div className="relative">
                            <div className="absolute inset-0 flex items-center">
                                <div className="w-full border-t border-secondary-200 dark:border-secondary-600" />
                            </div>
                            <div className="relative flex justify-center text-sm">
                                <span className="px-4 bg-white dark:bg-secondary-800 text-secondary-500 dark:text-secondary-400">
                                    {t('auth.register.orRegisterWith')}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Google Sign Up — consent via inline notice (common OAuth pattern) */}
                    <div className="mt-6">
                        <button
                            type="button"
                            onClick={handleGoogleSignUp}
                            disabled={isSubmitting || isLoading}
                            className="flex w-full items-center justify-center rounded-xl border border-secondary-300 bg-white px-4 py-3 transition-colors hover:bg-secondary-50 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:border-secondary-600 dark:bg-secondary-700 dark:hover:bg-secondary-600"
                        >
                            <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24">
                                <path
                                    fill="#4285F4"
                                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                                />
                                <path
                                    fill="#34A853"
                                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                                />
                                <path
                                    fill="#FBBC05"
                                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                                />
                                <path
                                    fill="#EA4335"
                                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                                />
                            </svg>
                            <span className="text-sm font-medium text-secondary-700 dark:text-secondary-300">Continue with Google</span>
                        </button>
                        <p className="mt-3 text-center text-xs leading-relaxed text-secondary-500 dark:text-secondary-400">
                            <Trans
                                i18nKey="auth.consent.googleContinueConsent"
                                components={{
                                    privacyLink: (
                                        <Link
                                            to="/privacy"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-brand-600 underline decoration-brand-600/40 underline-offset-2 hover:text-brand-500 dark:text-brand-400 dark:hover:text-brand-300"
                                        />
                                    ),
                                    termsLink: (
                                        <Link
                                            to="/terms"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-brand-600 underline decoration-brand-600/40 underline-offset-2 hover:text-brand-500 dark:text-brand-400 dark:hover:text-brand-300"
                                        />
                                    ),
                                }}
                            />
                        </p>
                    </div>
                </div>

                {/* Footer Links */}
                <div className="mt-6 text-center">
                    <Link to="/privacy" className="text-sm text-secondary-500 dark:text-secondary-400 hover:text-secondary-700 dark:hover:text-secondary-300 transition-colors">
                        {t('auth.privacyPolicy')}
                    </Link>
                </div>
            </div>
        </div>
    );
};

// Password requirement indicator component
const PasswordRequirement: React.FC<{ met: boolean; text: string }> = ({ met, text }) => (
    <div className="flex items-center text-xs">
        {met ? (
            <Check className="h-3.5 w-3.5 text-success-500 dark:text-success-400 mr-2" />
        ) : (
            <X className="h-3.5 w-3.5 text-secondary-400 dark:text-secondary-500 mr-2" />
        )}
        <span className={met ? 'text-success-700 dark:text-success-400' : 'text-secondary-500 dark:text-secondary-400'}>{text}</span>
    </div>
);

export default RegisterPage;
